/**
 * Bill Details Tour
 * Comprehensive walkthrough of bill detail page features
 *
 * This tour navigates through all four tabs (Summary, Analysis, Documents, Votes)
 * using onNextClick callbacks to switch tabs before advancing to content steps.
 */

import type { Tour } from '@/types/onboarding';

/**
 * Switch tab via custom event and advance to next step after React re-renders.
 * BillDetails listens for 'billbee:switch-tab' events to change tabs.
 */
function switchTabAndAdvance(tabValue: string, driverInstance: { moveNext: () => void }) {
  // Dispatch custom event for BillDetails to handle tab switch
  window.dispatchEvent(new CustomEvent('billbee:switch-tab', { detail: { tab: tabValue } }));

  // Wait for React to re-render tab content, then advance
  setTimeout(() => {
    driverInstance.moveNext();
  }, 250);
}

export const billDetailsTour: Tour = {
  id: 'bill-details',
  name: 'Understanding a Bill',
  description: 'Learn what each section of a bill page tells you',
  startRoute: '/bills/HB2438',
  steps: [
    // Header Section
    {
      element: '[data-tour="bill-header"]',
      popover: {
        title: 'Understanding this bill',
        description:
          'Every bill page starts with the essentials: bill number, a plain-language headline, and current status in the legislative process.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="bill-sponsors"]',
      popover: {
        title: 'Who introduced it',
        description:
          'See which legislators sponsored this bill. Click any name to view their full profile and other bills they support.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="track-bill-button"]',
      popover: {
        title: 'Track this bill',
        description:
          'Add any bill to your tracking list to receive updates when it moves through the legislative process.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="bill-beescore"]',
      popover: {
        title: 'BeeScore at a glance',
        description:
          'Our AI-generated impact score helps you quickly understand how significant this bill might be. Higher scores indicate greater potential impact.',
        side: 'bottom',
        align: 'center',
      },
    },

    // Tab Navigation
    {
      element: '[data-tour="bill-tabs"]',
      popover: {
        title: 'Four ways to explore',
        description:
          'Each tab offers different insights into the bill. Summary for the overview, Analysis for deep dives, Documents for original text, and Votes for legislative action.',
        side: 'bottom',
        align: 'center',
      },
    },

    // Summary Tab
    {
      element: '[data-tour="tab-summary"]',
      popover: {
        title: 'Summary tab',
        description:
          'Start here for a plain-language explanation of what the bill does and why it matters.',
        side: 'bottom',
        align: 'start',
        onNextClick: (_el, _step, { driver }) => switchTabAndAdvance('summary', driver),
      },
    },
    {
      element: '[data-tour="summary-content"]',
      popover: {
        title: 'What it means',
        description:
          "Our AI-generated summary breaks down complex legislative language into clear explanations of the bill's purpose, key changes, and potential effects.",
        side: 'top',
        align: 'start',
      },
    },

    // Analysis Tab
    {
      element: '[data-tour="tab-analysis"]',
      popover: {
        title: 'Analysis tab',
        description:
          "Dive deeper into the bill's potential impacts, fiscal implications, and who might be affected.",
        side: 'bottom',
        align: 'start',
        onNextClick: (_el, _step, { driver }) => switchTabAndAdvance('analysis', driver),
      },
    },
    {
      element: '[data-tour="analysis-breakdown"]',
      popover: {
        title: 'Score breakdown',
        description:
          "See exactly how we calculated the BeeScore. Each component reflects a different aspect of the bill's potential impact.",
        side: 'top',
        align: 'start',
      },
    },
    {
      element: '[data-tour="analysis-provisions"]',
      popover: {
        title: 'Key provisions',
        description:
          'The most important changes this bill would make, extracted and explained in plain language.',
        side: 'top',
        align: 'start',
        onNextClick: (_el, _step, { driver }) => switchTabAndAdvance('documents', driver),
      },
    },

    // Documents Tab
    {
      element: '[data-tour="tab-documents"]',
      popover: {
        title: 'Documents tab',
        description: 'Access the original bill text and any amendments in PDF format.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="pdf-viewer"]',
      popover: {
        title: 'Full bill text',
        description:
          'Read the actual legislative language. You can expand to fullscreen for easier reading.',
        side: 'top',
        align: 'center',
        onNextClick: (_el, _step, { driver }) => switchTabAndAdvance('votes', driver),
      },
    },

    // Votes Tab
    {
      element: '[data-tour="tab-votes"]',
      popover: {
        title: 'Votes tab',
        description:
          'See how legislators voted when the bill came up for a vote in either chamber.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="votes-section"]',
      popover: {
        title: 'Vote records',
        description:
          'View the complete voting record for House and Senate votes. See who voted yes, no, or abstained.',
        side: 'top',
        align: 'center',
      },
    },
  ],
};
